import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { usePlanes } from '@/hooks/usePlanes';
import EmpresaFormSteps from './EmpresaFormSteps';
import { 
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import toastService from '@/utils/toastService';
import revvlyLogo from '@/img/revvly.png';

/**
 * Onboarding completo y mejorado para crear una nueva empresa
 */
export const EmpresaOnboardingComplete = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { crearEmpresa, loading: empresaLoading, error: empresaError } = useSimpleEmpresa();
  const { planes } = usePlanes();
  
  // Si hay error de empresa pero estamos en onboarding, continuar de todos modos
  //console.log('🎯 Onboarding - Estado empresa:', { empresaLoading, empresaError });
  
  const [currentPhase, setCurrentPhase] = useState('welcome'); // welcome, form, creating, success
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('mensual');
  const [empresaExistente, setEmpresaExistente] = useState(false);
  const [formData, setFormData] = useState({
    // Datos precargados del usuario
    email: user?.primaryEmailAddress?.emailAddress || '',
    nombre: '',
    razon_social: '',
    rut: '',
    giro: '',
    telefono: '',
    direccion: '',
    ciudad: '',
    // Datos de sucursal principal
    sucursal_nombre: '',
    sucursal_direccion: '',
    sucursal_telefono: '',
    sucursal_horario: '',
    // Configuración adicional
    tagline: '',
    mensaje_bienvenida: '',
    mostrar_en_landing: false
  });
  const [formValid, setFormValid] = useState(false);
  const [error, setError] = useState(null);

  // Manejar cierre de sesión
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      toastService.error('Error al cerrar sesión');
    }
  };

  // Configurar automáticamente el plan gratuito
  React.useEffect(() => {
    if (planes.length > 0 && !selectedPlan) {
      const freePlan = planes.find(p => p.id === 'revvly_free' || p.price === 0);
      if (freePlan) {
        setSelectedPlan(freePlan);
      }
    }
  }, [planes, selectedPlan]);

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!formValid) return;

    setCurrentPhase('creating');
    setError(null);

    try {
      // Generar código único para la empresa
      const codigo_empresa = formData.nombre
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .substring(0, 8) + Date.now().toString().slice(-3);

      // Buscar el plan gratuito
      const freePlan = planes.find(p => p.id === 'revvly_free' || p.price === 0);
      const planToUse = freePlan || selectedPlan;

      const empresaData = {
        ...formData,
        codigo_empresa,
        plan_id: planToUse?.id || 'revvly_free',
        activa: true,
        subscription_status: 'active',
        trial_ends_at: null
      };

      const resultado = await crearEmpresa(empresaData);
      
      // Verificar si se accedió a una empresa existente
      if (resultado && resultado.mensaje && resultado.mensaje.includes('existente')) {
        //console.log('🏢 Accediendo a empresa existente:', resultado.mensaje);
        setEmpresaExistente(true);
        toastService.success(resultado.mensaje);
      }
      
      setCurrentPhase('success');
      
      // IMPORTANTE: Limpiar todos los flags de onboarding para permitir que el SafeEmpresaProvider use el provider real
      localStorage.removeItem('revvly_user_type');
      localStorage.removeItem('onboarding_completed');
      localStorage.removeItem('sucursalData');
      
      // Dar tiempo al contexto para actualizarse completamente
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Redirigir al dashboard después de un momento adicional
      setTimeout(() => {
        //console.log('🎯 Navegando al dashboard...');
        navigate('/dashboard');
      }, 2000);

    } catch (error) {
      console.error('Error creando empresa:', error);
      
      // Extraer mensaje de error más específico
      let errorMessage = 'Error al crear la empresa';
      
      if (error.message) {
        if (error.message.includes('Ya existe una empresa registrada con el RUT')) {
          errorMessage = error.message;
        } else if (error.message.includes('Ya existe una empresa registrada con el email')) {
          errorMessage = error.message;
        } else if (error.message.includes('duplicate key value violates unique constraint')) {
          if (error.message.includes('rut')) {
            errorMessage = 'Ya existe una empresa registrada con este RUT';
          } else if (error.message.includes('email')) {
            errorMessage = 'Ya existe una empresa registrada con este email';
          } else {
            errorMessage = 'Los datos ingresados ya están registrados en otra empresa';
          }
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
      setCurrentPhase('form');
      toastService.error(errorMessage);
    }
  };

  // Renderizar fase de bienvenida
  const renderWelcome = () => (
    <div className="text-center max-w-2xl mx-auto">
      <div className="mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-600 rounded-full mb-6">
          <SparklesIcon className="h-10 w-10 text-white" />
        </div>
        <h1 className="text-4xl font-bold text-white mb-4">
          ¡Bienvenido a Revvly! 🚀
        </h1>
        <p className="text-xl text-gray-300 leading-relaxed">
          Estás a pocos pasos de transformar la gestión de tu taller automotriz.
          Vamos a configurar tu empresa paso a paso.
        </p>
      </div>

      <div className="bg-gray-800 rounded-xl p-6 mb-8">
        <h3 className="text-lg font-semibold text-white mb-4">¿Qué incluye Revvly?</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
          <div className="flex items-start">
            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
            <div>
              <p className="text-white font-medium">Gestión Completa de Clientes</p>
              <p className="text-gray-400 text-sm">Base de datos centralizada y perfiles detallados</p>
            </div>
          </div>
          <div className="flex items-start">
            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
            <div>
              <p className="text-white font-medium">Control de Inventario</p>
              <p className="text-gray-400 text-sm">Repuestos y servicios con precios dinámicos</p>
            </div>
          </div>
          <div className="flex items-start">
            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
            <div>
              <p className="text-white font-medium">Calendario Inteligente</p>
              <p className="text-gray-400 text-sm">Agendamiento automático y recordatorios</p>
            </div>
          </div>
          <div className="flex items-start">
            <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
            <div>
              <p className="text-white font-medium">Reportes Avanzados</p>
              <p className="text-gray-400 text-sm">Analytics y métricas de rendimiento</p>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={() => setCurrentPhase('form')}
        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors text-lg"
      >
        Comenzar Configuración
      </button>
    </div>
  );


  // Renderizar formulario
  const renderForm = () => (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">Información de tu Empresa</h2>
        <p className="text-gray-300">
          Completa los datos para personalizar tu experiencia
        </p>
      </div>

      <EmpresaFormSteps
        formData={formData}
        onFormDataChange={setFormData}
        onValidationChange={setFormValid}
        onSubmit={handleSubmit}
      />

      {error && (
        <div className="mt-6 bg-red-900/30 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      )}

    </div>
  );

  // Renderizar estado de creación
  const renderCreating = () => (
    <div className="text-center max-w-lg mx-auto">
      <div className="mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">Creando tu empresa...</h2>
        <p className="text-gray-300">
          Estamos configurando todo para ti. Esto tomará solo unos segundos.
        </p>
      </div>

      <div className="bg-gray-800 rounded-lg p-6">
        <div className="space-y-3">
          <div className="flex items-center text-sm">
            <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
            <span className="text-gray-300">Creando empresa y configuración...</span>
          </div>
          <div className="flex items-center text-sm">
            <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
            <span className="text-gray-300">Configurando sucursal principal...</span>
          </div>
          <div className="flex items-center text-sm">
            <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
            <span className="text-gray-300">Aplicando configuración del plan...</span>
          </div>
          <div className="flex items-center text-sm">
            <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
            <span className="text-gray-300">Preparando tu dashboard...</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Renderizar éxito
  const renderSuccess = () => (
    <div className="text-center max-w-lg mx-auto">
      <div className="mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-green-600 rounded-full mb-6">
          <CheckCircleIcon className="h-12 w-12 text-white" />
        </div>
        <h2 className="text-3xl font-bold text-white mb-4">
          {empresaExistente ? '¡Acceso concedido! 🎉' : '¡Empresa creada exitosamente! 🎉'}
        </h2>
        <p className="text-gray-300 leading-relaxed">
          {empresaExistente ? (
            <>
              Ya tienes acceso a la empresa <strong className="text-white">{formData.nombre}</strong> con el <strong className="text-green-400">Plan Gratuito</strong>.
            </>
          ) : (
            <>
              Tu empresa <strong className="text-white">{formData.nombre}</strong> ha sido configurada con el <strong className="text-green-400">Plan Gratuito</strong>.
            </>
          )}
        </p>
      </div>

      <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-6 mb-8">
        <h3 className="text-green-400 font-semibold mb-3">Próximos pasos:</h3>
        <div className="space-y-2 text-left">
          <div className="flex items-center text-sm text-gray-300">
            <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2" />
            ✅ Sucursal principal configurada automáticamente
          </div>
          <div className="flex items-center text-sm text-gray-300">
            <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2" />
            Invitar mecánicos y personal
          </div>
          <div className="flex items-center text-sm text-gray-300">
            <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2" />
            Cargar servicios y precios
          </div>
          <div className="flex items-center text-sm text-gray-300">
            <CheckCircleIcon className="h-4 w-4 text-green-400 mr-2" />
            Comenzar a registrar clientes
          </div>
        </div>
      </div>

      <p className="text-gray-400 text-sm">
        Serás redirigido al dashboard en unos segundos...
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 relative">
          {/* Botón cerrar sesión - esquina superior derecha */}
          <div className="absolute top-0 right-0">
            <button
              onClick={handleSignOut}
              className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors bg-gray-800 hover:bg-gray-700 px-3 py-2 rounded-lg text-sm"
              title="Cerrar sesión"
            >
              <ArrowRightOnRectangleIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Cerrar sesión</span>
            </button>
          </div>

          <div className="flex items-center justify-center mb-4">
            <img 
              src={revvlyLogo} 
              alt="Revvly" 
              className="h-12 w-auto"
            />
          </div>
          
          {/* Indicador de progreso */}
          <div className="flex items-center justify-center space-x-4 mb-8">
            {['welcome', 'form', 'success'].map((phase, index) => (
              <div key={phase} className="flex items-center">
                <div className={`
                  w-3 h-3 rounded-full transition-colors
                  ${currentPhase === phase || (currentPhase === 'creating' && phase === 'form')
                    ? 'bg-blue-500' 
                    : index < ['welcome', 'form', 'success'].indexOf(currentPhase === 'creating' ? 'form' : currentPhase)
                      ? 'bg-green-500'
                      : 'bg-gray-600'
                  }
                `} />
                {index < 2 && <div className="w-8 h-px bg-gray-600 mx-2" />}
              </div>
            ))}
          </div>
        </div>

        {/* Contenido principal */}
        <div className="max-w-7xl mx-auto">
          {currentPhase === 'welcome' && renderWelcome()}
          {currentPhase === 'form' && renderForm()}
          {currentPhase === 'creating' && renderCreating()}
          {currentPhase === 'success' && renderSuccess()}
        </div>
      </div>
    </div>
  );
};

export default EmpresaOnboardingComplete;